/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import {declareEntity} from '../fw/MetaDataHelper.ts';
import * as Permission from "../util/Permission";

export const CACHING_STATUS_NONE = "none";
export const CACHING_STATUS_IN_PROGRESS = "in-progress";
export const CACHING_STATUS_FAILED = "caching-failed";
export const CACHING_STATUS_CACHED = "cached";
export const DELETE_FIELD_EXPRESSION = "$$||DELETE_FIELD||$$";

export default declareEntity({
    entity: "channels",
    graphQL: "Channel",
    name: "My Apps",
    dynamoPrefix: 'org',
    storageType: 'central',
    defaultOrder: ['name', 'type'],
    parentRelation: "channels",
    defaultTitle: (data) => {
        if (data['name']) {
            return data['name'];
        } else if (data['channelDef'] && data['channelDef']['name']) {
            return data['channelDef']['name'];
        }
    },
    defaultFilter: {
        activated: true
    },
    writePermission: [
        Permission.PERM_EDIT_CHANNEL,
    ],
    fields: (types) => { return {
        organisation: {
            name: "Organisation ID",
            type: "relation",
            to: () => types['organisations'],
            defaultValue: (organisation) => {
                return organisation['id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
        },
        id: {
            name: "My App ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            compositeKey: true,
        },
        name: {
            name: "Name",
            type: "string",
            defaultValue: "",
        },
        description: {
            name: "Description",
            type: "text",
            defaultValue: ""
        },
        implementationDetails: {
            name: "Implementation Details",
            type: "text",
            defaultValue: "",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        encryptKey: {
            name: "Encrypt Key",
            type: "string",
            userEditable: false,
            import: false,
            export: false,
            hideInApi: true,
            defaultValue: () => {
                return uuidv4().substring(0, 16).replace("-", "");
            },
            visibility: () => { return false; },
        },
        lockedAdding: {
            name: "Adding new integration can be only over API",
            type: "boolean",
            defaultValue: false,
            userEditable: true
        },
        externalId: {
            name: "External ID",
            type: "string",
            defaultValue: "",
            filterable: true,
        },
        type: {
            name: "Type",
            type: "string",
            defaultValue: "",
            validations: {
                matchRegexp: /^[a-zA-Z0-9\.\-_]+$/,
            }
        },
        cachingStatus: {
            name: "Caching status",
            type: "enum",
            defaultValue: "none",
            hideInApi: true,
            values: {
                [CACHING_STATUS_NONE]: {
                    name: "No caching",
                },
                [CACHING_STATUS_IN_PROGRESS]: {
                    name: "Caching in progress",
                },
                [CACHING_STATUS_FAILED]: {
                    name: "Caching failed",
                },
                [CACHING_STATUS_CACHED]: {
                    name: "Cached data",
                },
            }
        },
        cachingInProgressJob: {
            name: "Job for Caching in progress",
            to: () => types['jobs'],
            type: "relation",
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getCachingInProgressJob",
        },
        lastCachingRefresh: {
            name: "Last caching refresh",
            type: "datetime",
            userEditable: false,
            hideInApi: true,
        },
        rateLimit: {
            name: "Rate Limit",
            description: "Rate limit in format: 1000/m, user=1000/1m, global=1000/1h, 1000/1d (global=per all users, user=per user (default))",
            type: "string",
            defaultValue: "",
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        enabled: {
            name: "Enabled",
            type: "boolean",
            defaultValue: true,
            filterable: true,
        },
        confidential: {
            name: "Confidential",
            type: "boolean",
            defaultValue: false,
        },
        activated: {
            name: "Activated",
            type: "boolean",
            defaultValue: false,
            filterable: true,
            userEditable: false,
        },
        channelDef: {
            name: "Application Type",
            to: () => types['channelDefs'],
            type: "relation",
            required: true,
            filterable: true,
        },
        refreshFrequency: {
            name: "Refresh Frequency",
            type: "enum",
            values: {
				"none": {
					name: "No refreshing"
				},
                "generic": {
                    name: "Generic refresh frequency"
                }
            },
            userEditable: false,
            hideInApi: true,
			filterable: true,
        },
        instance: {
            name: "Instance",
            type: "relation",
            to: () => types['instances'],
            userEditable: false,
            hiddenCreate: true,
            defaultValue: (parent) => {
                if (parent) {
                    return parent['instance_id'];
                }
            },
        },
        metadata: {
            name: "Metadata",
            type: "object",
            userEditable: false,
            export: false,
            import: false,
            controllerFn: "getMetaData",
        },
        expiresIn: {
            name: "Expires in",
            description: "We need to delete unfinished channels after some time",
            type: "int",
            userEditable: false,
            export: false,
            import: false,
            defaultValue: () => {
                /* expire after 24 hours */
                return Math.floor(Date.now() / 1000) + 24 * 60 * 60;
            },
        },
        authExpiresIn: {
            name: "Auth expires in",
            type: "datetime",
            userEditable: false,
            import: false,
            export: false,
        },
        flowCount: {
            name: "Number of flows",
            type: "int",
            userEditable: false,
            export: false,
            import: false,
            defaultValue: 0,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        author: {
            name: "Created By",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        isAuthFilled: {
            name: "Is Authentication filled for current user",
            type: "boolean",
            userEditable: false,
            import: false,
            export: false,
            controllerFn: "getIsAuthFilled",
        },
        emailAuthExpires1: {
            name: "E-mail that auth expires in 14 days",
            type: "datetime",
            userEditable: false,
        },
        emailAuthExpires2: {
            name: "E-mail that auth expires in 7 day",
            type: "datetime",
            userEditable: false,
        },
        emailAuthExpires3: {
            name: "E-mail that auth expires in 1 day",
            type: "datetime",
            userEditable: false,
        },
        failedAuthAttempts: {
            name: "Failed auth attempts",
            type: "int",
            userEditable: false,
        },
        invalid: {
            name: "Is invalid",
            type: "boolean",
            userEditable: false,
        },
        authOptions: {
            name: "Authorization options",
            type: "object",
            export: false,
            import: false,
            userEditable: false,
            controllerFn: "getAuthOptions",
        },
        deleted: {
            name: "Deleted",
            type: "boolean",
            defaultValue: false,
			filterable: true,
        },
        deletedAt: {
            name: "Deleted At",
            type: "datetime",
            userEditable: false,
        },
        options: {
            name: "Options",
            type: "object",
            defaultValue: {},
        },
        agentLink: {
            name: "Agent Link",
            type: "string",
            defaultValue: "",
        },
        listeningFlows: {
            name: "Listening flows",
            type: "object",
            export: false,
            import: false,
            userEditable: false,
        },
        userOptions: {
            name: "User Options",
            type: "object",
            defaultValue: {},
            userEditable: false,
        },
        currentUserOptions: {
            name: "User Options",
            type: "object",
            controllerFn: "getCurrentUserOptions",
            controllerSetFn: "setCurrentUserOptions",
        },
    }; },
    relations: (types) => { return {
        jobs: {
            type: 'oneToMany',
            to: types['jobs'],
            foreignKeys: [
                {
                    from: "organisation_id|id",
                    to: "organisation_channel"
                }
                /*{
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }*/
            ]
        },
        customDatas: {
            type: 'oneToMany',
            to: types['customDatas'],
            IndexName: 'organisation_channel_flow-key-index',
            foreignKeys: [
                {
                    from: "organisation_id|id",
                    to: "organisation_channel_flow",
                }
            ]
        },
        organisationEvents: {
            type: 'manyToOne',
            to: types['organisationEvents'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }
            ],
        },
        dataSets: {
            type: 'handler',
            to: types['dataSets'],
        },
        storeInfos: {
            type: 'oneToMany',
            to: types['storeInfos'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                },
            ]
        },
        events: {
            type: 'oneToMany',
            to: types['events'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }
            ]
        },
        eventDetectors: {
            type: 'oneToMany',
            to: types['eventDetectors'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }
            ]
        },
        hooks: {
            type: 'oneToMany',
            to: types['hooks'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    asFilterOnly: true,
                    to: "channel_id"
                }
            ]
        },
        screens: {
            type: 'oneToMany',
            to: types['screens'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }
            ]
        },
        relationDefs: {
            type: 'oneToMany',
            to: types['relationDefs'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    asFilterOnly: true,
                    to: "fromChannel_id"
                }
            ]
        },
        relationDefs2: {
            type: 'oneToMany',
            to: types['relationDefs'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    asFilterOnly: true,
                    to: "toChannel_id"
                }
            ]
        },
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "channel_id"
                }
            ]
        },
        usedScenarioScreenDefsToUse: {
            type: 'oneToMany',
            to: types['usedScenarioWithScreenDefs'],
            args: {
                operation: {
                    type: "string"
                },
                channelEntityDef: {
                    type: "string"
                },
            },
            controllerFn: 'getUsedScenarioScreenDefsToUse',
        },
        flows: {
            type: 'oneToMany',
            to: types['flows'],
            controllerFn: 'getFlows',
        },
        relevantScenarios: {
            type: 'oneToMany',
            to: types['scenarios'],
            args: {
                onlyActiveChannels: {
                    type: 'boolean',
                },
                first: {
                    type: 'int'
                },
                requiredPayPlan: {
                    type: 'string'
                },
                channelEntityDefId: {
                    type: 'string'
                },
                nameLike: {
                    type: 'string'
                }
            },
            controllerFn: 'getRelevantScenarios',
        },
        channelAddons: {
            type: 'oneToMany',
            to: types['channelAddons'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
        logger: {
            type: 'oneToMany',
            to: types['loggers'],
            foreignKeys: [
                {
                    from: "id",
                    to: "channel_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },
        notifications: {
            type: 'oneToMany',
            to: types['organisationNotifications'],
            foreignKeys: [
                {
                    from: "id",
                    to: "channel_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },
        channelErrors: {
            type: 'oneToMany',
            to: types['channelErrors'],
            foreignKeys: [
                {
                    from: "id",
                    to: "channel_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },

    }; }
});
